import React, {useEffect} from 'react';
import {FormControl, Grid} from '@mui/material';
import {numericFormatter} from 'react-number-format';
import {useAccountContext} from '../../../contexts/AccountContext';
import {
  ActionType,
  AutoField,
  AutoFieldProps,
  AutoTextField,
  SelectField,
} from '../../../components/AutoForm/AutoFieldNew';
import {DAFeeFieldType, FeeType} from './builderFields';
import Loading from '../../../components/Loading/Loading';
import {useBuilderContext} from './useBuilderContext';
import {UPDATES} from '../../../shared/UPDATES';
import {useAutoField} from '../../../components/AutoForm/useAutoForm';

const feeField: AutoTextField = {
  // here and not-exported to prevent other components using it
  id: 'fee',
  label: 'Initial Fee',
  type: ActionType.text,
  number: true,
  labelOnTop: true,
  allowZero: true,
  allowNegative: false,
  decimals: 2,
};

export const getFeeFormikFromServer = (
  isSppDa: boolean,
  serverStr?: string | null
): {
  feeType: FeeType;
  fixedFee: string;
  percentageFee: string;
  fee: string;
} | null => {
  if (!isSppDa) {
    return null;
  }
  if (!serverStr || !serverStr.length) {
    return {feeType: '%', fixedFee: '', percentageFee: '', fee: '0'};
  }
  const hasGbp = serverStr.includes('GBP');
  if (hasGbp) {
    const value = serverStr.replace('GBP', '').trim();
    return {
      feeType: 'GBP',
      fixedFee: numericFormatter(value, {decimalScale: 2, fixedDecimalScale: true}),
      percentageFee: '',
      fee: numericFormatter(value, {decimalScale: 2}),
    };
  }
  const value = serverStr.replace('%', '').trim();
  return {
    feeType: '%',
    percentageFee: Number(value).toFixed(2),
    fixedFee: '',
    fee: value,
  };
};

export const getFeeServerFromFormik = (
  isSppDa: boolean,
  fields: DAFeeFieldType | undefined | null
): string | undefined => {
  if (!isSppDa) {
    return undefined;
  }
  if (!fields) {
    return '0%';
  }
  if (fields.feeType === 'GBP') {
    return `${(fields.fixedFee || fields.fee || '0')
      .replaceAll('£', '')
      .replaceAll('GBP', '')} GBP`;
  }
  return `${(fields.percentageFee || fields.fee || '0').replaceAll('%', '')}%`;
};

export const DAInitialBuilderFeeField: React.FC<
  {
    formikKeyPrefixWithFee: string;
    employers?: boolean;
  } & Partial<AutoFieldProps>
> = ({formikKeyPrefixWithFee: formikKeyPrefix, employers = false, disabled}) => {
  // How this works:
  // the fee field is now JUST used for loading up server values into the form.
  // Then this is converted into either fixedFee or percentageFee
  // The frontend then needs to convert them back to the values.
  const AmbiguousFeeKey = `${formikKeyPrefix}.fee`;
  const feeTypeKey = `${formikKeyPrefix}.feeType`;
  const fixedFeeKey = `${formikKeyPrefix}.fixedFee`;
  const percentageFeeKey = `${formikKeyPrefix}.percentageFee`;
  const {account, accountLoading} = useAccountContext();
  const {builderDetails} = useBuilderContext();
  const [fixedBuilderFeeField, fixedFeeMeta, fixedFeeHelpers] =
    useAutoField<string>(fixedFeeKey);
  const [percentageBuilderFeeField, percentageFeeMeta, percentageFeeHelpers] =
    useAutoField<string>(percentageFeeKey);
  const [feeTypeField, feeTypeMeta, feeTypeHelpers] = useAutoField<'GBP' | '%' | null>(
    feeTypeKey
  );

  function setFeeType(type: 'GBP' | '%' | null) {
    feeTypeHelpers.setValue(UPDATES._D1504, type);
  }
  const feeType = feeTypeField.value;

  useEffect(() => {
    // Checking if the fee is disabled must be the top condition!!!
    if (disabled) {
      // No initial fee on contribution / transferring into a LISA
      fixedFeeHelpers.setValue(UPDATES._D1500, '');
      percentageFeeHelpers.setValue(UPDATES._D1501, '');
    } else if (builderDetails || !account) {
      // no need to do anything else
    } else if (account.initialFixedFee) {
      // fixed amount fee
      fixedFeeHelpers.setValue(UPDATES._D1502, `${account.initialFixedFee}`);
      percentageFeeHelpers.setValue(UPDATES._D1501, '');
      setFeeType('GBP');
    } else {
      // percentage fee
      fixedFeeHelpers.setValue(UPDATES._D1500, '');
      percentageFeeHelpers.setValue(UPDATES._D1503, `${account.initialPercentageFee}`);
      setFeeType('%');
    }
  }, [builderDetails, account?.initialFixedFee, account?.initialPercentageFee, disabled]);

  useEffect(() => {
    const percentageFeeNumber = Number(percentageBuilderFeeField.value);
    if (feeTypeField.value !== '%' || Number.isNaN(percentageFeeNumber)) {
      return;
    }
    /**
     * Valid values should be between 0 - 100
     */
    const validValue = Math.max(Math.min(percentageFeeNumber, 100), 0);
    if (validValue !== percentageFeeNumber) {
      percentageFeeHelpers.setValue(UPDATES._D1505, String(validValue));
    }
  }, [percentageBuilderFeeField.value, feeTypeField.value]);

  if (!account) {
    if (accountLoading) {
      return (
        <Grid container spacing={2}>
          <Loading entityName="fees" />
        </Grid>
      );
    }
    return null;
  }

  return (
    <>
      <Grid container alignContent="center" spacing={1}>
        {/* <DisplayIfEnv envs={['UAT', 'DEV', 'TEST']}>
          This is a non-functional demo of the new version */}
        <Grid item xs={4}>
          <FormControl
            data-test={employers ? 'employers-fee-type' : 'personal-fee-type'}
            fullWidth
            variant="filled"
            size="small"
          >
            <AutoField
              field={{
                type: ActionType.select,
                id: feeTypeKey,
                fullWidth: true,
                label: 'Initial Fee Type',
                options: [
                  {
                    label: '£',
                    value: 'GBP',
                  },
                  {
                    label: '%',
                    value: '%',
                  },
                ],
              }}
              disabled={disabled}
            />
          </FormControl>
        </Grid>
        <Grid data-test={employers ? 'employers-fee' : 'personal-fee'} item xs={8}>
          <AutoField
            // bugfix: when switching fee types, the field shows the same value but the state is not updated
            key={feeTypeField.value}
            field={{
              ...feeField,
              label: employers ? 'Employer Initial Fee' : feeField.label,
              id: feeType === '%' ? percentageFeeKey : fixedFeeKey,
            }}
            disabled={disabled}
          />
        </Grid>
      </Grid>
    </>
  );
};
